import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, IndianRupee, CreditCard, Calendar, Hash } from 'lucide-react';
import { toast } from 'sonner';
import { apiMultipart } from '../utils/api';

const CustomerPaymentModal = ({ payment, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    payment_type: 'full',
    amount_paid: '',
    transaction_reference: '',
    notes: ''
  });
  const [proofFile, setProofFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (payment) {
      setFormData(prev => ({
        ...prev,
        amount_paid: payment.amount.toString()
      }));
    }
  }, [payment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('payment_type', formData.payment_type);
      formDataToSend.append('amount_paid', formData.amount_paid);
      formDataToSend.append('transaction_reference', formData.transaction_reference);
      formDataToSend.append('notes', formData.notes);
      if (proofFile) {
        formDataToSend.append('proof', proofFile);
      }

      await apiMultipart.post(`/payments/${payment.payment_id}/submit-payment`, formDataToSend);
      
      toast.success('Payment submitted for verification');
      setFormData({ payment_type: 'full', amount_paid: '', transaction_reference: '', notes: '' });
      setProofFile(null);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ payment_type: 'full', amount_paid: payment?.amount?.toString() || '', transaction_reference: '', notes: '' });
    setProofFile(null);
    onClose();
  };

  if (!payment) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-lg">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">Make Payment</DialogTitle>
          <DialogDescription className="text-stone-500">
            Submit payment details for verification
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2" data-testid="customer-payment-form">
          {/* Payment Summary */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">Milestone</span>
              <span className="font-medium text-stone-900">{payment.stage_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600 flex items-center">
                <IndianRupee className="h-3.5 w-3.5 mr-1" />
                Amount Due
              </span>
              <span className="text-xl font-bold text-orange-700">
                ₹{payment.amount.toLocaleString('en-IN')}
              </span>
            </div>
            {payment.due_date && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-stone-600 flex items-center">
                  <Calendar className="h-3.5 w-3.5 mr-1" />
                  Due Date
                </span>
                <span className="font-medium text-stone-900">
                  {new Date(payment.due_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Payment Type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-stone-700 flex items-center">
              <CreditCard className="h-4 w-4 mr-2 text-stone-400" />
              Payment Type
            </Label>
            <Select
              value={formData.payment_type}
              onValueChange={(value) => {
                const newAmount = value === 'full' ? payment.amount.toString() : '';
                setFormData({ ...formData, payment_type: value, amount_paid: newAmount });
              }}
            >
              <SelectTrigger className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full Payment</SelectItem>
                <SelectItem value="partial">Partial Payment</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount" className="text-sm font-medium text-stone-700 flex items-center">
              <IndianRupee className="h-4 w-4 mr-2 text-stone-400" />
              Amount Paid
            </Label>
            <Input
              id="amount"
              type="number"
              value={formData.amount_paid}
              onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
              placeholder="Enter amount"
              className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg"
              required
              disabled={formData.payment_type === 'full'}
              data-testid="payment-amount-input"
            />
          </div>

          {/* Transaction Reference */}
          <div className="space-y-2">
            <Label htmlFor="transaction_ref" className="text-sm font-medium text-stone-700 flex items-center">
              <Hash className="h-4 w-4 mr-2 text-stone-400" />
              Transaction Reference / UTR
            </Label>
            <Input
              id="transaction_ref"
              value={formData.transaction_reference}
              onChange={(e) => setFormData({ ...formData, transaction_reference: e.target.value })}
              placeholder="e.g., UTR123456789012"
              className="h-11 border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg font-mono"
              data-testid="transaction-ref-input"
            />
          </div>

          {/* Payment Proof Upload */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-stone-700">
              Payment Proof (Optional)
            </Label>
            <div className="border-2 border-dashed border-stone-200 rounded-lg p-4 text-center hover:border-stone-300 transition-colors">
              <Input
                id="proof"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setProofFile(e.target.files[0])}
                className="hidden"
                data-testid="payment-proof-input"
              />
              <label htmlFor="proof" className="cursor-pointer">
                {proofFile ? (
                  <div className="flex items-center justify-center text-stone-700">
                    <FileText className="h-6 w-6 mr-2 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-sm">{proofFile.name}</p>
                      <p className="text-xs text-stone-500">
                        {(proofFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                    <p className="text-sm text-stone-600">Upload payment screenshot</p>
                    <p className="text-xs text-stone-400 mt-1">PDF, JPG or PNG</p>
                  </>
                )}
              </label>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium text-stone-700">
              Notes (Optional)
            </Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional information..."
              rows={2}
              className="border-stone-200 focus:border-stone-400 focus:ring-stone-400 rounded-lg resize-none"
              data-testid="payment-notes-input"
            />
          </div>

          {/* Info Box */}
          <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
            <p className="text-sm text-stone-600 leading-relaxed">
              Your payment will be verified by the admin. A receipt will be generated and sent to you after verification.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              disabled={submitting}
              data-testid="submit-payment-button"
            >
              {submitting ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center">
                  <Upload className="mr-2 h-4 w-4" />
                  Submit Payment
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerPaymentModal;
